/**
 * Retorno del OAuth de calendario cuando se abre en una ventana emergente.
 *
 * Antes este flujo navegaba la pestaña entera al backend. Al volver, la SPA se
 * montaba de cero y había que reconstruir a mano en qué vacante y en qué paso
 * estaba el candidato, leyendo banderas de `localStorage`. Una de esas banderas
 * (`calendar_connected_<usuario>`) se quedaba puesta, así que a partir de ahí
 * cualquier montaje saltaba al final del proceso.
 *
 * Con la ventana emergente la pestaña original nunca se desmonta: no hay estado
 * que reconstruir. El popup sólo tiene que avisar y cerrarse.
 */

export const TIPO_MENSAJE_CALENDARIO = 'calendario-oauth';

/** Los `status` con los que el backend devuelve el control al frontend. */
export const ESTADOS_CALENDARIO = [
  'calendar_connected',
  'calendar_error',
  'calendar_failure',
  'calendar_denied',
] as const;

export type EstadoCalendario = (typeof ESTADOS_CALENDARIO)[number];

export interface MensajeCalendario {
  tipo: typeof TIPO_MENSAJE_CALENDARIO;
  estado: EstadoCalendario;
}

/** Lo mínimo de `window` que se usa aquí, para poder probarlo sin navegador. */
export interface VentanaMinima {
  opener: { postMessage: (mensaje: unknown, origenDestino: string) => void } | null;
  location: { search: string; origin: string };
  close: () => void;
}

export function estadoCalendarioEnUrl(search: string): EstadoCalendario | null {
  const estado = new URLSearchParams(search).get('status');
  return ESTADOS_CALENDARIO.includes(estado as EstadoCalendario)
    ? (estado as EstadoCalendario)
    : null;
}

/**
 * Si esta ventana es la emergente del OAuth de calendario, avisa a quien la
 * abrió y se cierra.
 *
 * Devuelve `true` en ese caso, para que la aplicación NO se monte aquí: hacerlo
 * enseñaría el flujo de postulación dentro del popup durante un instante y, si
 * el cierre fallara, dejaría dos copias de la SPA compitiendo por el mismo
 * `localStorage`.
 */
export function cerrarSiEsPopupDeCalendario(ventana: VentanaMinima = window): boolean {
  if (!ventana.opener) return false;

  const estado = estadoCalendarioEnUrl(ventana.location.search);
  if (!estado) return false;

  const mensaje: MensajeCalendario = { tipo: TIPO_MENSAJE_CALENDARIO, estado };
  // Destino explícito y no '*': el mensaje viaja a la pestaña de la persona
  // candidata justo después de un consentimiento, y no tiene por qué ser
  // legible desde otro origen.
  ventana.opener.postMessage(mensaje, ventana.location.origin);
  ventana.close();
  return true;
}
